// Запуск игры внутри платформ: мини-приложение Telegram, VK Mini Apps (VK Игры), Яндекс Игры.
// Определяем платформу при старте (параметры запуска запоминаются на сессию), подключаем SDK,
// получаем подписанные данные игрока — сервер проверяет подпись и входит/регистрирует без почты.

export type PlatformName = 'telegram' | 'vk' | 'yandex';

interface TelegramWebApp {
  initData: string;
  ready: () => void;
  expand: () => void;
  openInvoice?: (url: string, cb?: (status: string) => void) => void;
  disableVerticalSwipes?: () => void;
}
interface YaPlayer {
  signature: string;
  getMode: () => string;
}
interface YaSdk {
  getPlayer: (o: { signed: boolean }) => Promise<YaPlayer>;
  auth: { openAuthDialog: () => Promise<void> };
  features?: { LoadingAPI?: { ready: () => void } };
}
declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
    YaGames?: { init: () => Promise<YaSdk> };
  }
}

const KEY = 'sk_platform';
const VK_KEY = 'sk_vk_launch';
let ysdk: YaSdk | null = null;

function session(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

/** Платформа, в которой открыта игра (null — обычный сайт). */
export function detectPlatform(): PlatformName | null {
  const s = session();
  const q = new URLSearchParams(location.search);
  let p = (s?.getItem(KEY) as PlatformName | null) ?? null;
  if (q.get('tg') === '1' || location.hash.includes('tgWebAppData')) p = 'telegram';
  else if (q.has('vk_app_id') && q.has('sign')) {
    p = 'vk';
    s?.setItem(VK_KEY, location.search.slice(1));
  } else if (q.get('platform') === 'yandex' || import.meta.env.VITE_PLATFORM === 'yandex') p = 'yandex';
  if (p) s?.setItem(KEY, p);
  return p;
}

export const platform: PlatformName | null = typeof window !== 'undefined' ? detectPlatform() : null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`script ${src}`));
    document.head.appendChild(el);
  });
}

/** VK Bridge без библиотеки: сообщение родительскому окну vk.com. */
function vkSend(handler: string, params: Record<string, unknown> = {}): void {
  try {
    window.parent.postMessage({ handler, params, type: 'vk-connect' }, '*');
  } catch {
    // не в iframe
  }
}

/** Подключить SDK платформы (вызывается один раз при старте). */
export async function initPlatform(): Promise<void> {
  try {
    if (platform === 'telegram') {
      if (!window.Telegram?.WebApp) await loadScript('https://telegram.org/js/telegram-web-app.js');
      const wa = window.Telegram?.WebApp;
      wa?.ready();
      wa?.expand();
      wa?.disableVerticalSwipes?.();
    } else if (platform === 'vk') {
      vkSend('VKWebAppInit');
    } else if (platform === 'yandex') {
      // SDK лежит на домене Яндекса (у страницы <base> указывает на сервер игры — путь абсолютный)
      if (!window.YaGames) await loadScript(`${location.origin}/sdk.js`);
      ysdk = (await window.YaGames?.init()) ?? null;
      ysdk?.features?.LoadingAPI?.ready();
    }
  } catch {
    // SDK недоступен — игра работает как обычный сайт
  }
}

/** Подписанные данные игрока для входа (null — платформа не дала данных). */
export async function platformData(): Promise<string | null> {
  if (platform === 'telegram') return window.Telegram?.WebApp?.initData || null;
  if (platform === 'vk') return session()?.getItem(VK_KEY) ?? null;
  if (platform === 'yandex' && ysdk) {
    let player = await ysdk.getPlayer({ signed: true });
    if (player.getMode() === 'lite') {
      // Гость Яндекса: просим войти, чтобы прогресс сохранялся
      await ysdk.auth.openAuthDialog().catch(() => undefined);
      player = await ysdk.getPlayer({ signed: true });
    }
    return player.getMode() === 'lite' ? null : player.signature;
  }
  return null;
}

/** Оплата внутри Telegram: счёт Stars открывается окном мини-приложения. */
export function openTelegramInvoice(url: string, onClose: (status: string) => void): boolean {
  const wa = window.Telegram?.WebApp;
  if (platform !== 'telegram' || !wa?.openInvoice) return false;
  wa.openInvoice(url, onClose);
  return true;
}
